import React, { forwardRef, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { twMerge } from "tailwind-merge";
import { PiWarningCircleLight } from 'react-icons/pi';
import { containsEmoji, containsDisallowedCharacters } from '../../../utils/validation/validators';
import { removeEmojis, removeSpecialSymbols } from '../../../utils/validation/sanitizers';

interface InputProps {
  type: string;
  id?: string;
  name?: string;
  label?: string;
  placeholder?: string;
  value?: string | number;
  defaultValue?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  min?: number;
  required?: boolean;
  className?: string;
  error?: string | boolean;
  helperText?: string | false | undefined;
  disabled?: boolean;
  description?: string;
  italicTxt?: string;
  pattern?: string;
  icon?: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  infoText?: string;
  infoTrigger?: 'hover' | 'click';
  allowEmojis?: boolean;
  sanitizeEmojis?: boolean;
  autoFocus?: boolean;
}

export function cn(...inputs: any[]) {
  return twMerge(clsx(inputs));
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type,
      id,
      name,
      label,
      placeholder,
      value,
      defaultValue,
      onChange,
      onBlur,
      onKeyDown,
      min,
      required,
      className,
      error,
      helperText,
      disabled,
      italicTxt,
      description,
      pattern,
      icon,
      prefix,
      suffix,
      infoText,
      infoTrigger = 'hover',
      allowEmojis = false,
      sanitizeEmojis = true,
      autoFocus = false,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [openInfo, setOpenInfo] = useState(false);
    const [emojiError, setEmojiError] = useState<string>();
    const hasInfo = Boolean(infoText);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = e.target.value;

      // Check for emojis if not allowed
      if (!allowEmojis && sanitizeEmojis && newValue) {
        if (containsEmoji(newValue)) {
          setEmojiError(t('Emojis are not allowed in this field'));
          // Auto-remove emojis
          newValue = removeEmojis(newValue);
        } else if (containsDisallowedCharacters(newValue)) {
          setEmojiError(t('Invalid characters detected'));
          newValue = removeSpecialSymbols(newValue);
        } else {
          setEmojiError(undefined);
        }

        // Update the input value to the sanitized version
        e.target.value = newValue;
      }

      // Call the original onChange handler
      if (onChange) {
        onChange(e);
      }
    };

    const toggleClick = () => {
      if (infoTrigger === 'click') setOpenInfo((o) => !o);
    };
    const openHover = () => {
      if (infoTrigger === 'hover') setOpenInfo(true);
    };
    const closeHover = () => {
      if (infoTrigger === 'hover') setOpenInfo(false);
    };

    return (
      <div className="flex-1">
        {(label || hasInfo) && (
          <div className="mb-2 flex items-center gap-1">
            {label && (
              <label htmlFor={id} className="block text-xs text-gray-700 dark:text-gray-300">
                {t(label)}
              </label>
            )}

            {hasInfo && (
              <div
                className="relative inline-flex items-center"
                onMouseEnter={openHover}
                onMouseLeave={closeHover}
              >
                <button
                  type="button"
                  onClick={toggleClick}
                  aria-label="Info"
                  className={clsx(
                    'ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 dark:border-gray-600 text-[10px] leading-none',
                    'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition'
                  )}
                >
                  i
                </button>

                {openInfo && (
                  <div
                    className="absolute z-50 mt-2 w-64 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 text-xs text-gray-700 dark:text-gray-200 shadow-lg"
                    role="tooltip"
                  >
                    {t(infoText!)}
                    <div className="absolute -top-1 left-3 h-2 w-2 rotate-45 bg-white dark:bg-gray-800 border-l border-t border-gray-200 dark:border-gray-700" />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* <div className="flex flex-col space-y-1 mb-2"> */}
          {italicTxt && (
            <p className="text-xs text-gray-500 dark:text-gray-300 text-left italic">{t(italicTxt)}</p>
          )}
          {description && (
            <p className="text-sm text-gray-700 dark:text-gray-200 text-left">{t(description)}</p>
          )}
        {/* </div> */}

        <div className="relative">

          <input
            type={type}
            id={id}
            name={name}
            ref={ref}
            className={clsx(
              'block w-full px-3 py-2 bg-white dark:bg-gray-900 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 transition duration-300 input text-xs',
              suffix ? 'pr-10' : 'pr-3',
              error || emojiError ? 'ring-red-500' : 'ring-grayscale-500',
              'focus:ring-primary-500 dark:focus:ring-primary-700',
              className
            )}
            placeholder={placeholder}
            value={value}
            defaultValue={defaultValue}
            onChange={handleChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            min={min}
            required={required}
            disabled={disabled}
            pattern={pattern}
            autoFocus={autoFocus}
            {...rest}
          />
        </div>

        {(error || emojiError) && (helperText || emojiError) && (
          <div className="mt-1 flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
            <PiWarningCircleLight className="h-4 w-4 text-red-500 flex-shrink-0" />
            <span>{emojiError || helperText}</span>
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
